import { Component, OnInit, OnDestroy, signal, ChangeDetectorRef, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { CartService, CartResponse } from '../../core/services/cart.service';
import { GuestSession } from '../../core/services/guest-session';
import { AuthService } from '../../core/services/auth.service';
import { WebAppConfigService } from '../../core/services/webapp-config.service';
import { API_CONFIG } from '../../core/config/api.config';
import { CommonModule } from '@angular/common';
import { environment } from '../../../environments/environment';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-header',
  imports: [CommonModule],
  templateUrl: './header.html',
  styleUrl: './header.css',
})
export class Header implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  isCartOpen = false;
  isAccountMenuOpen = false;
  cartCount = signal<number>(0);
  cartData = signal<CartResponse['data']>(null);
  isLoadingCart = signal<boolean>(false);
  timeRemaining = signal<string>('');
  private countdownInterval: any;
  
  // Logo dinámico desde configuración webapp - usar string directamente
  get logoUrl(): string {
    return this.webAppConfig.getLogoUrl();
  }

  envName = environment.envName;
  isNonProd = !environment.production;

  constructor(
    private cartService: CartService,
    private guestSession: GuestSession,
    public authService: AuthService,
    public webAppConfig: WebAppConfigService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (!this.isBrowser) {
      this.cartCount.set(0);
      this.cartData.set(null);
      return;
    }

    this.loadCartCount();
    
    // Validar sesión del usuario si no hay estado local y cookie_http_only está activo
    // Esto es especialmente importante al recargar la página con cookies httpOnly
    if (!this.authService.currentUser() && API_CONFIG.cookie_http_only) {
      this.authService.getProfile().subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.authService.setCurrentUserFromProfile(response.data.profile);
            console.log('✅ Sesión validada en header');
          }
        },
        error: () => {
          // No hay sesión activa, continuar como invitado
          console.log('ℹ️ No hay sesión activa en header');
        }
      });
    }
    
    // Suscribirse a notificaciones de tickets agregados
    this.cartService.ticketsAdded$.subscribe(() => {
      console.log('🛒 Tickets agregados al carrito - abriendo cart');
      this.openCart();
    });
  }

  ngOnDestroy(): void {
    this.clearCountdown();
  }

  loadCartCount(): void {
    if (!this.isBrowser) {
      this.cartCount.set(0);
      this.cartData.set(null);
      return;
    }

    const sessionId = this.guestSession.getSessionId();
    
    // Verificar si hay sesión activa (funciona en modo cookie y manual)
    if (!this.guestSession.isSessionActive()) {
      // Si no hay sesión activa, asegurar sesión (reusar existente o crear nueva)
      this.guestSession.ensureSession().subscribe({
        next: (response) => {
          if (response.success) {
            // En modo cookie, sessionId será null (correcto)
            this.loadCartBySessionId(this.guestSession.getSessionId());
          }
        },
        error: () => {
          this.cartCount.set(0);
        }
      });
      return;
    }
    
    // Cargar carrito con sessionId (null en modo cookie)
    this.loadCartBySessionId(sessionId);
  }

  private loadCartBySessionId(sessionId: string | null): void {
    this.cartService.getCartBySession(sessionId).subscribe({
      next: (response) => {
        setTimeout(() => {
          if (response.success && response.data) {
            this.cartCount.set(response.data.ticketCount || response.data.tickets.length);
            this.cartData.set(response.data);
            this.startCountdown(response.data.expiresAt);
          } else {
            this.cartCount.set(0);
            this.cartData.set(null);
            this.clearCountdown();
          }
        }, 0);
      },
      error: (e) => {
        console.log(e);
        setTimeout(() => {
          this.cartCount.set(0);
          this.cartData.set(null);
          this.clearCountdown();
        }, 0);
      }
    });
  }

  openCart(): void {
    this.isCartOpen = true;
    this.loadCartContent();
  }

  loadCartContent(): void {
    const sessionId = this.guestSession.getSessionId();
    
    // Verificar si hay sesión activa (funciona en modo cookie y manual)
    if (!this.guestSession.isSessionActive()) {
      this.cartData.set(null);
      this.clearCountdown();
      return;
    }

    this.isLoadingCart.set(true);
    this.cartService.getCartBySession(sessionId).subscribe({
      next: (response) => {
        setTimeout(() => {
          if (response.success && response.data) {
            this.cartData.set(response.data);
            this.cartCount.set(response.data.ticketCount || response.data.tickets.length);
            this.startCountdown(response.data.expiresAt);
          } else {
            this.cartData.set(null);
            this.cartCount.set(0);
            this.clearCountdown();
          }
          this.isLoadingCart.set(false);
        }, 0);
      },
      error: () => {
        setTimeout(() => {
          this.cartData.set(null);
          this.cartCount.set(0);
          this.clearCountdown();
          this.isLoadingCart.set(false);
        }, 0);
      }
    });
  }

  getTotalPrice(): number {
    const cart = this.cartData();
    if (!cart?.ticketsDetailed) return 0;
    return cart.ticketsDetailed.reduce((sum, ticket) => sum + (ticket.precio_pagado || 0), 0);
  }

  private startCountdown(expiresAt: string): void {
    if (!this.isBrowser) return;
    this.clearCountdown();
    this.updateTimeRemaining(expiresAt);
    this.countdownInterval = setInterval(() => {
      this.updateTimeRemaining(expiresAt);
    }, 1000);
  }

  private updateTimeRemaining(expiresAt: string): void {
    const now = new Date().getTime();
    const expiry = new Date(expiresAt).getTime();
    const diff = expiry - now;

    if (diff <= 0) {
      this.timeRemaining.set('Expirado');
      this.clearCountdown();
      return;
    }

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    this.timeRemaining.set(`${minutes}:${seconds.toString().padStart(2, '0')}`);
  }

  private clearCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    this.timeRemaining.set('');
  }

  closeCart(): void {
    this.isCartOpen = false;
  }

  removeTicket(ticketid: string): void {
    const cart = this.cartData();
    if (!cart || !cart.cartid) return;

    this.cartService.removeTicket(cart.cartid, ticketid).subscribe({
      next: (response) => {
        if (response && !response.success) {
          Swal.fire({
            icon: 'warning',
            title: 'No se pudo eliminar el ticket',
            text: response.message || 'Ocurrió un error inesperado',
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#e8343f'
          });
          return;
        }
        // Recargar el carrito
        this.loadCartContent();
      },
      error: (error) => {
        console.error('Error removing ticket:', error);
        const message = error?.error?.message || error?.message || 'Error al eliminar el ticket del carrito';
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: message,
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#e8343f'
        });
      }
    });
  }

  proceedToCheckout(): void {
    this.closeCart();
    const cart = this.cartData();
    const slug = cart?.evento?.url_slug;
    if (slug) {
      this.router.navigate(['/evento', slug, 'checkout']);
    } else {
      this.router.navigate(['/']);
    }
  }

  toggleAccountMenu(): void {
    this.isAccountMenuOpen = !this.isAccountMenuOpen;
  }

  closeAccountMenu(): void {
    this.isAccountMenuOpen = false;
  }

  goToLogin(): void {
    this.closeAccountMenu();
    this.router.navigate(['/login']);
  }

  goToProfile(): void {
    this.closeAccountMenu();
    this.router.navigate(['/profile']);
  }

  goToHome(): void {
    this.router.navigate(['/']);
  }

  logout(): void {
    this.closeAccountMenu();
    this.authService.logout().subscribe({
      next: () => {
        this.router.navigate(['/']);
      },
      error: () => {
        // Incluso si falla la petición, limpiar localmente
        this.authService.clearSession();
        this.router.navigate(['/']);
      }
    });
  }
}
